import React from "react";
import { Check, ChevronRight, X } from "lucide-react";
import type { SetupPaso, SetupPasoId } from "../hooks/useHomeBanners";
import { CampoDeEstrellas } from "./CreditsTopBar";
import { TiendaNubeIconCompacto } from "./TiendaNubeIcon";

/**
 * La tarjeta de "Configurá tu cuenta" de la home: los pasos que le quedan a una
 * cuenta recién paga, con progreso — ADR-2026-08-27-tarjeta-configuracion-cuenta-paga.
 *
 * Antes cada paso era un cartel suelto que aparecía cuando le tocaba y no sabía
 * de los otros: el usuario nunca veía que era un recorrido, ni cuánto le faltaba,
 * y completar uno se sentía como que el cartel "se fue" y apareció otro. Acá los
 * dos están siempre a la vista —el hecho tildado, el actual con su acción, el que
 * viene atenuado— y la barra muestra el avance.
 *
 * Es PRESENTACIONAL: quién resuelve si un paso está hecho es `useHomeBanners`.
 * Acá vive el copy de cada paso, que es lo único que cambia si mañana se suma un
 * tercero (agregar el `id` al tipo y una entrada en `COPY`).
 */

/** Lo que se le dice al usuario de cada paso. El orden lo pone el hook. */
const COPY: Record<
  SetupPasoId,
  {
    titulo: string;
    /** Cómo se llama la tarea una vez HECHA: el pendiente pide algo ("Conectá…") y
     *  el completado informa un estado ("Conectado a…"). Es la misma tarea, así que
     *  el texto cambia de tiempo verbal en vez de sólo apagarse. */
    tituloHecho: string;
    descripcion: string;
    cta: string;
    /** La acción secundaria ("Ahora no") de un paso que se puede dejar para
     *  después. Sólo la tienen los pasos `omitible` — ver `PASOS_OMITIBLES`. */
    ctaOmitir?: string;
    minutos: string;
  }
> = {
  marca: {
    titulo: "Contanos más sobre tu marca",
    tituloHecho: "Ya nos contaste sobre tu marca",
    // El "para qué" y no el "qué": el paso pide trabajo, así que tiene que decir
    // qué gana el usuario a cambio.
    descripcion:
      "Colores, estilo y contexto: con eso Atlas genera fotos que parecen tuyas y no genéricas.",
    cta: "Describir mi marca",
    minutos: "2 min",
  },
  tiendanube: {
    titulo: "Conectá tu Tienda Nube",
    tituloHecho: "Conectado a Tienda Nube",
    descripcion:
      "Importá tus productos y creá contenido para tu tienda en segundos.",
    cta: "Conectar",
    // "Ahora no" y no "No me interesa" / "Omitir": no todo el mundo tiene una
    // tienda hoy, y el que la tenga en un mes puede conectarla desde Mi marca. El
    // copy dice que es una postergación, que es lo que realmente pasa.
    ctaOmitir: "Ahora no",
    minutos: "1 min",
  },
};

/**
 * Ícono de la tarea de marca (Font Awesome 7, `list-ul`). Vive acá y no en
 * `design-system/icons` porque ese set es cerrado y curado; si mañana lo usa otra
 * pantalla, se muda allá. `currentColor` para que lo pinte la fila, como el de
 * Tienda Nube.
 */
const MarcaIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 20 20"
    className={className}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3.5 6.5C4.32812 6.5 5 5.82812 5 5C5 4.17188 4.32812 3.5 3.5 3.5C2.67188 3.5 2 4.17188 2 5C2 5.82812 2.67188 6.5 3.5 6.5ZM8 4C7.44687 4 7 4.44687 7 5C7 5.55313 7.44687 6 8 6H17C17.5531 6 18 5.55313 18 5C18 4.44687 17.5531 4 17 4H8ZM8 9C7.44687 9 7 9.44687 7 10C7 10.5531 7.44687 11 8 11H14C14.5531 11 15 10.5531 15 10C15 9.44687 14.5531 9 14 9H8ZM8 14C7.44687 14 7 14.4469 7 15C7 15.5531 7.44687 16 8 16H17C17.5531 16 18 15.5531 18 15C18 14.4469 17.5531 14 17 14H8ZM3.5 16.5C4.32812 16.5 5 15.8281 5 15C5 14.1719 4.32812 13.5 3.5 13.5C2.67188 13.5 2 14.1719 2 15C2 15.8281 2.67188 16.5 3.5 16.5ZM5 10C5 9.17188 4.32812 8.5 3.5 8.5C2.67188 8.5 2 9.17188 2 10C2 10.8281 2.67188 11.5 3.5 11.5C4.32812 11.5 5 10.8281 5 10Z" />
  </svg>
);

/**
 * El ícono de cada tarea. Toma `currentColor`, así que se pinta con el `text-*` de
 * la fila.
 */
const ICONO: Record<SetupPasoId, React.FC<{ className?: string }>> = {
  marca: MarcaIcon,
  tiendanube: TiendaNubeIconCompacto,
};

/**
 * Progreso que se mostró por última vez en ESTA carga de la página (fracción de
 * 0 a 1). Arranca en 0, así que la primera vez la barra entra animándose desde
 * vacío; y cuando el usuario vuelve de completar un paso, la barra viaja del
 * valor anterior al nuevo en lugar de aparecer ya avanzada. Sin esto, el avance
 * —que es el premio del paso que acaba de hacer— no se ve nunca.
 *
 * A nivel módulo por la misma razón que los flags del hook: la tarjeta se monta
 * dos veces por pantalla (mobile + desktop) y se rehace al navegar.
 *
 * Va con la `clave` de la cuenta que lo dibujó (el `id_company`): otra pestaña puede
 * cambiar la sesión sin recargar ésta, y la barra de la cuenta nueva no tiene por qué
 * arrancar desde el progreso de la anterior
 * (ADR-2026-09-15-el-store-de-notificaciones-tiene-dueno).
 */
let progresoMostradoEnEstaCarga: { clave: string | null; valor: number } = {
  clave: null,
  valor: 0,
};

export const HomeSetupCard: React.FC<{
  pasos: SetupPaso[];
  /** Arranca el paso (navegar a describir la marca / abrir el modal de TN). */
  onPaso: (id: SetupPasoId) => void;
  /** "Ahora no": deja el paso para después. Sin esto, el botón no se dibuja. */
  onOmitir?: (id: SetupPasoId) => void;
  className?: string;
  /** Quita el margen inferior (para layouts en fila, no apilados). */
  noMargin?: boolean;
  /** De quién es el progreso (el `id_company`), para no arrancar la animación desde el de otra cuenta. */
  clave?: string | null;
}> = ({ pasos, onPaso, onOmitir, className, noMargin, clave = null }) => {
  const total = pasos.length;
  // Una tarea OMITIDA no suma: el usuario la dejó para después, no la completó, y
  // la barra tiene que seguir diciendo la verdad. Queda "1 de 2" con una fila
  // omitida —que es exactamente lo que pasó— en vez de un "2 de 2" que se
  // atribuye trabajo que nadie hizo (ADR-2026-09-15-ahora-no-en-tienda-nube).
  const hechos = pasos.filter((p) => p.hecho && !p.omitido).length;
  /* Antes sólo el PRIMER paso pendiente tenía descripción y botón, y los de abajo
     se veían apagados y sin acción, para que se leyera como un recorrido guiado. Se
     dio de baja a pedido: las dos tareas son independientes —conectar la tienda no
     necesita que la marca esté descrita— y hacer esperar a alguien que quiere
     resolver la segunda primero es fabricar un orden que el producto no tiene.
     Ahora TODA tarea pendiente está activa. */

  const [ancho, setAncho] = React.useState(
    progresoMostradoEnEstaCarga.clave === clave ? progresoMostradoEnEstaCarga.valor : 0,
  );
  React.useEffect(() => {
    const objetivo = total ? hechos / total : 0;
    // Dos `requestAnimationFrame`: en el primero el navegador todavía no pintó el
    // ancho inicial, así que cambiarlo ahí no dispara la transición — salta.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        progresoMostradoEnEstaCarga = { clave, valor: objetivo };
        setAncho(objetivo);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [hechos, total, clave]);

  const mb = noMargin ? "" : "mb-4";

  return (
    <div className={className}>
      {/*
        Fondo azul de marca que panea, con las estrellitas de la barra de créditos
        (`.barra-creditos-degrade` + `CampoDeEstrellas`, ADR-2026-08-04-la-barra-de-creditos-respira):
        es el mismo tratamiento que ya usa el otro bloque "vivo" de la home, así que
        no inventa un lenguaje nuevo. Sobre el gris de la pantalla —donde todo lo
        demás es blanco sobre gris— esto es lo único que se mueve y lo único que
        pide algo.

        `relative`: es el marco de referencia de la capa de estrellas, que va
        absoluta y hereda el radio. El contenido lleva `z-10` para pasar SIEMPRE por
        encima de ellas.
      */}
      <div
        className={`${mb} barra-creditos-degrade relative overflow-hidden rounded-2xl bg-brand-primary p-5 sm:p-6`}
      >
        <CampoDeEstrellas />
        <div className="relative z-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
          <div className="flex min-w-0 items-center">
            <div className="min-w-0">
              <h3 className="text-[17px] font-bold text-white sm:text-[18px]">
                Ya sos parte de Estudio Atlas
              </h3>
              {/* Fijo, no una cuenta regresiva ("te queda un paso"): cuánto falta ya
                  lo dicen la barra y el "N de 2" que tiene al lado. */}
              <p className="text-[13px] font-medium leading-snug text-white/75 sm:text-[14px]">
                Configurá tu cuenta para obtener mejores resultados
              </p>
            </div>
          </div>
          {/* La barra va A LA DERECHA del título, no al pie: el encabezado dice qué
              es esto y la barra dice cuánto falta, que es la misma pregunta. En
              mobile no hay ancho para las dos cosas en una fila, así que baja. */}
          <div className="w-full shrink-0 sm:w-[320px]">
        <div
          /* Blanco translúcido sobre el azul: sin relleno no hay nada que mirar, y
             sin una pista visible el usuario no sabe que hay una barra hasta que
             completa el primer paso, justo cuando más sirve mostrarle que el
             recorrido tiene principio. */
          className="h-1.5 w-full overflow-hidden rounded-full bg-white/25 sm:mt-4"
          role="progressbar"
          aria-valuenow={hechos}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-label={`Configuración de la cuenta: ${hechos} de ${total} pasos`}
        >
          <div
            className="setup-barra-progreso h-full rounded-full bg-white"
            style={{ width: `${ancho * 100}%` }}
          />
        </div>
        {/* El recuento en palabras, alineado con el arranque de la barra: la barra
            sola dice "vas por acá" pero no cuántas cosas son. */}
        <p className="mt-1.5 text-[12px] font-semibold text-white/60">
          {hechos} de {total}{" "}
          {total === 1 ? "tarea completada" : "tareas completadas"}
        </p>
          </div>
        </div>

        {/* Una tarjeta POR TAREA, separadas, y no una lista con divisores: cada
            tarea es una cosa que se completa por su cuenta, y así el tilde y el
            atenuado del que ya está hecho se leen sobre su propio bloque.

            Azul SÓLIDO apenas más claro que el fondo (#3A66F3, el tono claro del
            degradé subido un punto) y sin borde: se despega lo justo. Opaco y no
            translúcido a propósito — con el degradé paneando por debajo, un panel de
            vidrio cambia de color solo mientras el usuario lee. */}
        <ol className="relative z-10 mt-4 space-y-3 sm:mt-5">
          {pasos.map((paso) => {
            const copy = COPY[paso.id];
            const Icono = ICONO[paso.id];
            const pendiente = !paso.hecho;
            return (
              <li
                key={paso.id}
                /* La tarea hecha se apoya en blanco al 6% —translúcido, así que deja
                   pasar el degradé y las estrellas— y no en un azul sólido: apenas se
                   despega del fondo, que es lo que corresponde a algo que ya no pide
                   nada. La pendiente sí es un bloque opaco. */
                className={`overflow-hidden rounded-xl ${
                  pendiente
                    ? "bg-brand-primary-claro"
                    : // En MOBILE las completadas no se muestran: en una pantalla
                      // donde la tarjeta compite con el resto de la home, el lugar
                      // es para lo que falta hacer. El avance sigue contado por la
                      // barra y por "N de N tareas completadas", que no se van.
                      "hidden bg-[#ffffff10] sm:block"
                }`}
              >
                <div
                  className={`flex flex-col gap-2.5 px-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4 ${
                    pendiente ? "py-3 sm:py-3.5" : "py-2.5"
                  }`}
                >
                  <div className="flex min-w-0 items-start gap-2.5">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {Icono && (
                          <Icono
                            /* `-mr-0.5`: le come 2px al `gap-x-2` de la fila sin
                               tocar la separación con el chip de minutos, que usa
                               el mismo gap y ahí sí hace falta entero. */
                            className="-mr-0.5 h-[18px] w-[18px] shrink-0 text-white"
                          />
                        )}
                        <h4
                          className={`text-[13.5px] leading-snug sm:text-[14.5px] ${
                            pendiente
                              ? "font-bold text-white"
                              : "font-semibold text-white"
                          }`}
                        >
                          {/* La omitida conserva el título en tiempo PENDIENTE
                              ("Conectá tu Tienda Nube"): `tituloHecho` informa un
                              estado que no pasó —decía "Conectado a Tienda Nube"
                              al lado de "Tarea omitida", o sea las dos cosas a la
                              vez—. La tarea sigue siendo la misma, sin hacer. */}
                          {paso.omitido || pendiente
                            ? copy.titulo
                            : copy.tituloHecho}
                        </h4>
                        {/* Cuánto cuesta: el paso se posterga por miedo a que sea
                            largo, y son dos minutos. Sólo en el actual y en los que
                            faltan — en uno hecho ya no informa nada. */}
                        {!paso.hecho && (
                          <span className="rounded-md bg-white/15 px-1.5 py-0.5 text-[11px] font-semibold text-white/70">
                            {copy.minutos}
                          </span>
                        )}
                      </div>
                      {/* La descripción sólo si está pendiente: en una tarea ya
                          hecha es ruido y estira la tarjeta al doble. Y en MOBILE no
                          va nunca (`hidden sm:block`): a 390px se come tres líneas
                          por tarea y empuja el CTA fuera de la primera pantalla; el
                          título ya dice qué es cada tarea. */}
                      {pendiente && (
                        <p className="mt-0.5 hidden text-[12.5px] font-medium leading-snug text-white/70 sm:block sm:text-[13.5px]">
                          {copy.descripcion}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Texto subrayado, sin caja. Pasó por sólido → ghost con filo →
                      esto: con DOS tareas activas al mismo tiempo, cualquier botón
                      con caja las convertía en dos bloques compitiendo entre sí y con
                      el resto de la home. Sin caja, el peso lo lleva la tarjeta azul y
                      cada tarea se lee como una línea con su acción. El subrayado es
                      lo que lo mantiene reconocible como algo cliqueable. */}
                  {/* Donde iba el CTA, el estado. Texto plano y NO un botón apagado:
                      una tarea terminada no tiene nada a dónde llevar, y un link sin
                      destino es una promesa que la fila no puede cumplir. */}
                  {!pendiente && (
                    <span className="inline-flex shrink-0 items-center gap-1.5 text-[14px] font-semibold text-white">
                      {/* El tilde vive ACÁ, pegado a su texto, y no como marcador al
                          principio de la fila: son una sola cosa —"esto ya está"— y
                          separados obligaban a leer los dos extremos para entenderlo. */}
                      {!paso.omitido && (
                        <Check
                          className="setup-tilde h-4 w-4"
                          strokeWidth={3}
                          aria-hidden="true"
                        />
                      )}
                      {paso.omitido ? "Tarea omitida" : "Tarea completada"}
                    </span>
                  )}
                  {pendiente && (
                    /* "Ahora no" va a la IZQUIERDA del CTA y sin subrayado: las dos
                       son acciones de la misma fila, pero sólo una es la que la
                       tarea pide. El subrayado —lo único que marca "esto se
                       clickea" en una tarjeta sin botones con caja— se lo queda el
                       CTA; el "Ahora no" se apoya en un blanco más apagado y sube
                       de opacidad al hover, así que se encuentra cuando se lo
                       busca y no compite cuando no. */
                    <div className="flex shrink-0 items-center gap-1 self-start sm:gap-2 sm:self-auto">
                      {copy.ctaOmitir && paso.omitible && onOmitir && (
                        <button
                          type="button"
                          onClick={() => onOmitir(paso.id)}
                          className="inline-flex h-8 w-auto items-center justify-center rounded-lg px-2 text-[13px] font-medium text-white/55 transition-colors hover:bg-white/10 hover:text-white/85 focus:outline-none focus:ring-2 focus:ring-white/70 sm:h-10 sm:text-[14px]"
                        >
                          {copy.ctaOmitir}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onPaso(paso.id)}
                        className="inline-flex h-8 w-auto items-center justify-center gap-1.5 rounded-lg px-1 text-[13px] font-semibold text-white underline decoration-white/50 decoration-2 underline-offset-4 transition-colors hover:decoration-white focus:outline-none focus:ring-2 focus:ring-white sm:h-10 sm:text-[14px]"
                      >
                        {copy.cta}
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

/**
 * La felicitación: se muestra UNA vez, cuando el usuario termina el último paso.
 * Verde y no azul a propósito — es el único cartel de la home que no pide nada,
 * y el color lo dice antes que el texto.
 */
export const HomeSetupExito: React.FC<{
  onCerrar?: () => void;
  className?: string;
  noMargin?: boolean;
}> = ({ onCerrar, className, noMargin }) => {
  const mb = noMargin ? "" : "mb-4";
  return (
    <div className={className}>
      <div
        className={`${mb} flex items-center gap-3 rounded-2xl border border-[#A7F3D0] bg-success-soft p-4`}
        role="status"
      >
        <span className="setup-tilde flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success text-white">
          <Check className="h-5 w-5" strokeWidth={3} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-semibold text-success-text sm:text-[15px]">
            ¡Listo! Tu cuenta quedó configurada
          </h3>
          <p className="text-[13px] leading-snug text-[#065F46]/70 sm:text-[14px]">
            Ya podés generar contenido con la identidad de tu marca y tus
            productos.
          </p>
        </div>
        {onCerrar && (
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="-mr-1 shrink-0 rounded-lg p-1.5 text-[#065F46]/60 transition-colors hover:bg-[#B9F0D8] hover:text-success-text"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
